import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { Search as SearchIcon } from '@mui/icons-material';
import { format, startOfMonth, subYears } from 'date-fns';
import toast from 'react-hot-toast';
import ReportTransactionsTable from '../components/reports/ReportTransactionsTable';
import { reportsAPI } from '../api/reports';
import { LoadingSpinner } from '../components/common';

export default function MonthlyReport() {
  const [startDate, setStartDate] = useState(() => {
    // Start date is first day of month from 1 year ago
    const today = new Date();
    const lastYear = subYears(today, 1);
    return startOfMonth(lastYear);
  });
  const [endDate, setEndDate] = useState(() => startOfMonth(new Date()));
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);

  // Calculate client-side totals from transaction data
  const totals = useMemo(() => {
    if (!reportData) return null;

    const calculateTotals = (transactions) => {
      if (!transactions || transactions.length === 0) {
        return { subtotal: 0, vat: 0, total: 0 };
      }

      return transactions.reduce(
        (acc, transaction) => ({
          subtotal: acc.subtotal + parseFloat(transaction.subtotal || 0),
          vat: acc.vat + parseFloat(transaction.vat || 0),
          total: acc.total + parseFloat(transaction.total || 0)
        }),
        { subtotal: 0, vat: 0, total: 0 }
      );
    };

    return {
      cashSales: calculateTotals(reportData.cash_sales),
      refunds: calculateTotals(reportData.refunds),
      invoices: calculateTotals(reportData.invoices),
      creditNotes: calculateTotals(reportData.credit_notes)
    };
  }, [reportData]);

  const handleSearch = async () => {
    if (!startDate || !endDate) {
      toast.error('Please select both start and end months');
      return;
    }

    if (startDate > endDate) {
      toast.error('Start month must be before end month');
      return;
    }

    setLoading(true);
    try {
      // Backend parses these with PHP's DateTime::createFromFormat('F Y', ...)
      // - must be "<full month name> <4-digit year>", e.g. "July 2025".
      const params = {
        start: format(startDate, "MMMM yyyy"),
        end: format(endDate, "MMMM yyyy")
      };

      const response = await reportsAPI.getMonthlyReport(params);
      setReportData(response.data);
      toast.success('Report loaded successfully');
    } catch (error) {
      console.error('Report error:', error);
      toast.error('Failed to load report');
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" sx={{ mb: 3 }}>
          Monthly Report
        </Typography>

        {/* Filters */}
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 4 }}>
              <DatePicker
                label="Start Month"
                value={startDate}
                onChange={setStartDate}
                views={['year', 'month']}
                format="MMMM yyyy"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small'
                  }
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              <DatePicker
                label="End Month"
                value={endDate}
                onChange={setEndDate}
                views={['year', 'month']}
                format="MMMM yyyy"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small'
                  }
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                disabled={loading}
                fullWidth
              >
                Search
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Loading State */}
        {loading && <LoadingSpinner />}

        {/* Report Results */}
        {!loading && reportData && totals && (
          <Box>
            <ReportTransactionsTable
              title="Cash Sales"
              data={reportData.cash_sales}
              totals={totals.cashSales}
            />

            <ReportTransactionsTable
              title="Refunds"
              data={reportData.refunds}
              totals={totals.refunds}
            />

            <ReportTransactionsTable
              title="Account Invoices"
              data={reportData.invoices}
              totals={totals.invoices}
            />

            <ReportTransactionsTable
              title="Credit Notes"
              data={reportData.credit_notes}
              totals={totals.creditNotes}
            />
          </Box>
        )}

        {/* Empty State */}
        {!loading && !reportData && (
          <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              Select a month range and click Search to view the monthly report
            </Typography>
          </Paper>
        )}
      </Box>
    </LocalizationProvider>
  );
}
